import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import videojs from 'video.js';
import faker from '@faker-js/faker';
import { SeekButton, QualitySelectorButton } from '@skooldio/skooldio-video-player';
import '@skooldio/skooldio-video-player/dist/skd-videojs.css';

import { Box } from 'Components/Base';

import VjsComponentWrapper from './VjsComponentWrapper';

const COMPONENT_TYPES = {
  // These are default components from vidojs.
  // See videojs Default Component Tree for more components.
  // https://docs.videojs.com/tutorial-components.html#default-component-tree
  PLAY_TOGGLE: 'PlayToggle',
  VOLUME_PANEL: 'VolumePanel',
  PROGRESS_CONTROL: 'ProgressControl',
  CURERNT_TIME_DISPLAY: 'CurrentTimeDisplay',
  DURATION_DISPLAY: 'DurationDisplay',
  PLAYBACK_RATE_MENU_BUTTON: 'PlaybackRateMenuButton',
  FULL_SCREEN_TOGGLE: 'FullscreenToggle',

  // Components from plugin.
  // See skooldio-vieo-player plugin for more information.
  QUALITY_SELECTOR_BUTTON: 'QualitySelectorButton',
  SEEK_BUTTON: 'SeekButton',
};

const getVjsBaseComponent = (type) => {
  switch (type) {
    case COMPONENT_TYPES.SEEK_BUTTON:
      // Get component from SeekButton plugins exported from skooldio-video-player lib
      return SeekButton;
    case COMPONENT_TYPES.QUALITY_SELECTOR_BUTTON:
      return QualitySelectorButton;
    default:
      // Get component from videojs
      return videojs.getComponent(type);
  }
};

const createComponent = (player, componentType, options) => {
  const VjsBaseComponent = getVjsBaseComponent(componentType);
  return player && VjsBaseComponent ? new VjsBaseComponent(player, options) : null;
};

const VjsComponent = ({ player, componentType, options, name, children, ...restProps }) => {
  let buttonContainer;
  let Component;

  const handleDisposeComponent = () => {
    if (Component && Component.el()) {
      Component.dispose();
    }
  };

  useEffect(() => {
    if (player) {
      Component = createComponent(player, componentType, options);
      buttonContainer.appendChild(Component.el());
      player.on('dispose', handleDisposeComponent);
    }

    return handleDisposeComponent;
  }, [player]);

  if (!player) return null;

  return (
    <Box style={{ height: 'fit-content', paddingTop: '8px' }} {...restProps}>
      <VjsComponentWrapper
        data-testid={`btn-${name}-player`}
        className={'video-js vjs-workinghover vjs-v6 vjs-seek-buttons'}
        ref={(node) => {
          buttonContainer = node;
        }}
      >
        {children}
      </VjsComponentWrapper>
    </Box>
  );
};

VjsComponent.COMPONENT_TYPES = COMPONENT_TYPES;
VjsComponent.propTypes = {
  player: PropTypes.object,
  componentType: PropTypes.string,
  options: PropTypes.object,
  name: PropTypes.string,
};

VjsComponent.defaultProps = {
  name: faker.random.uuid(),
};

export default VjsComponent;
